/**
 * Maps the doctor-finder autocomplete result-type arrays into the item shape
 * consumed by `<sp-quantum-select navigation="accordion">`.
 *
 * The shared omnisearch store (`apps/shared/store/omnisearch-store.js`) fetches
 * the result-type arrays, concatenates them in `doctor_finder_omnisearch_result_types`
 * order, and hands the combined list to `buildQuantumSelectItems`. The store and
 * every consumer (`<sp-finder-omnisearch>`, the specialty/provider facets) import
 * this module directly.
 *
 * Input item shape — only the keys this adapter reads:
 *   { type, text, value, group, visibility, filter?, subtext?,
 *     children?(bool parent flag), parent?(parent value), parentName?,
 *     iconClass?, referralRequired?, icon?(url), url?, filters?, query?, modality?,
 *     navigateOnSelect? }
 *
 * `visibility` drives idle-vs-typing display, which the accordion container has
 * no native concept of (its only filter is a title substring match):
 *   - 'no-query'  → browsable when the input is empty (common specialties,
 *                   recent searches, the all-specialty parent groupings)
 *   - 'query'     → only while the user is typing (individual searchable
 *                   specialties, conditions, physicians, locations, …)
 *   - null/absent → always shown (e.g. categories, scoped specialties)
 *
 * Selection routing stays in the wizard: every node carries a `payload` with the
 * fields `WizardStep1#handleAutocompleteSelect` already routes on (url / filters
 * / filter+value / query / navigateOnSelect / referralRequired / type).
 */

// Routing fields lifted onto each node's `payload`. The quantum-select stays
// agnostic of what they mean; the wizard reads them back on selection.
const PAYLOAD_KEYS = ["url", "filters", "filter", "value", "query", "type", "navigateOnSelect", "referralRequired"];

// Generic fallback glyph for a top-level row the server did not give a composed
// icon and that has no thumbnail — i.e. non-specialty result types (conditions,
// symptoms, treatments, …): a white "care-filled" glyph on the themeable default
// circle. Specialty colours are NOT keyed here: the server composes each
// specialty's descriptor from `specialties.icon_color` and ships it as
// `prependIcon`. The circle colour stays a CSS variable so each client can theme
// the generic fallback.
const GENERIC_DEFAULT_PREPEND_ICON = {
  name: "care-filled",
  color: "#fff",
  background: { shape: "circle", color: "var(--sp-comp-omni-search-default-icon-bg, #b2d9dc)" },
};

// A row whose `modality` is `video` (a saved search filtering on video visit
// types) gets a video-visit glyph on the themeable default circle. The glyph is
// primary-coloured (not white like the generic default) so it reads against the
// light-teal circle.
export const VIDEO_VISIT_PREPEND_ICON = {
  name: "video-visit",
  color: "var(--sp-sys-color-primary)",
  background: { shape: "circle", color: "var(--sp-comp-omni-search-default-icon-bg, #b2d9dc)" },
};

// Themeable circle behind a physician/location avatar, shown behind a glyph when
// there is no photo and behind transparent regions of a real portrait.
const AVATAR_BACKGROUND = "var(--sp-comp-omni-search-default-icon-bg, #b2d9dc)";

/**
 * Build the `prependAvatar` descriptor for a physician / location row. The server
 * ships `raw.icon` as a thumbnail URL — a real portrait for entries that have one,
 * or a default placeholder SVG (`portrait-default` / `location-default`) otherwise.
 * Render real portraits as a circular cover image and placeholders as the matching
 * glyph (person / map-pin) on the themeable circle. The glyph also serves as the
 * on-error fallback when a portrait URL fails to load.
 */
function buildAvatarDescriptor(raw) {
  const icon = typeof raw.icon === "string" ? raw.icon : "";
  const isLocation = raw.type === "Location" || icon.includes("location-default");
  const isPlaceholder = /(?:portrait|location)-default/.test(icon);

  const descriptor = {
    icon: isLocation ? "map-pin-filled" : "user-alt-filled",
    fillColor: "#fff",
    color: AVATAR_BACKGROUND,
  };

  if (!isPlaceholder) {
    descriptor.image = raw.icon;
  }

  return descriptor;
}

/**
 * Build the quantum-select item arrays for both display states.
 *
 * @param {Array<Object>} rawItems - flattened autocomplete items, already
 *   concatenated in result-type order.
 * @returns {{ idleItems: Array<Object>, searchItems: Array<Object> }}
 *   `idleItems` are shown while the input is empty; `searchItems` while typing.
 *   Both are nested (parents carry an inline `children` array).
 */
export function buildQuantumSelectItems(rawItems) {
  const items = (Array.isArray(rawItems) ? rawItems : []).filter(Boolean);

  // 'no-query' and null are visible when idle; 'query' and null while typing.
  const idleItems = nestItems(items.filter((item) => item.visibility !== "query").map(mapRawItem));
  const searchItems = nestItems(items.filter((item) => item.visibility !== "no-query").map(mapRawItem));

  return { idleItems, searchItems };
}

/**
 * Map a single source item to a quantum-select node. Internal linkage fields are
 * prefixed with `_` so they never collide with the component's own item keys.
 */
function mapRawItem(raw) {
  const node = {
    title: raw.text,
    value: nodeValue(raw),
    payload: buildPayload(raw),
    // Parent linkage is reconstructed in `nestItems`; kept private here.
    _rawValue: raw.value,
    _parent: raw.parent,
  };

  if (raw.subtext) {
    node.subtitle = raw.subtext;
  }

  if (raw.group) {
    node.group = raw.group;
  }

  // Prepend visual, by source field in precedence order: `prependIconUrl` (a
  // self-coloured brand SVG, no circle), `prependIcon` (a server-composed sprite
  // name or `{name, color, background}` glyph-on-circle), a thumbnail `icon` →
  // circular `prependAvatar`, then the video-visit / generic-default glyphs. The
  // server's `iconClass` is ignored (can't cross the sp-list shadow boundary);
  // children get no glyph.
  if (raw.prependIconUrl) {
    node.props = { prependIconUrl: raw.prependIconUrl };
  } else if (raw.prependIcon) {
    node.props = { prependIcon: raw.prependIcon };
  } else if (raw.icon) {
    node.props = { prependAvatar: buildAvatarDescriptor(raw) };
  } else if (raw.parent == null) {
    node.props = { prependIcon: raw.modality === "video" ? VIDEO_VISIT_PREPEND_ICON : GENERIC_DEFAULT_PREPEND_ICON };
  }

  // The quantum-select filters on title only, so the server sends the item's other searchable
  // strings (accent-free name, NPI, aliases) as `keywords`, which the container also matches.
  if (raw.keywords) {
    node.keywords = raw.keywords;
  }

  // `showFilters` is the server's array of human-readable filter labels for a
  // saved search (e.g. ["Children", "Video Visit", "New Patient"], from
  // `SavedDoctorFinderSearch#as_tags`). Forward them to sp-list-item's `tags`
  // prop, which renders a read-only chip row beneath the title.
  if (Array.isArray(raw.showFilters) && raw.showFilters.length) {
    node.tags = raw.showFilters;
  }

  return node;
}

/**
 * Unique, stable per-item value. Skill items carry a real `value`; physician /
 * location and free-text items route by url/filters, so synthesise a key that is
 * still unique within the list.
 */
function nodeValue(raw) {
  if (raw.value != null) {
    return raw.value;
  }
  if (raw.url) {
    return raw.url;
  }
  return `${raw.type ?? "item"}|${raw.text ?? ""}`;
}

/** Lift the defined routing fields onto a compact payload object. */
function buildPayload(raw) {
  const payload = {};
  for (const key of PAYLOAD_KEYS) {
    if (raw[key] !== undefined) {
      payload[key] = raw[key];
    }
  }
  return payload;
}

/**
 * Composite nesting key: the same specialty value can appear as a parent in more
 * than one group (e.g. "Primary Care" is an idle parent under both "Common
 * Specialties" and "All Specialties", each with its own child set). Keying purely
 * on the value would collapse them — children would all attach to whichever
 * occurrence the map saw last, leaving the other rendered childless (no chevron).
 * Scoping the linkage by `(group, value)` keeps each occurrence's children with it.
 */
function linkKey(group, value) {
  return `${group ?? ""}::${value}`;
}

/**
 * Normalise a child title into a dedup key: case-folded and whitespace-collapsed
 * so labels that differ only in casing/spacing ("Family Medicine" vs "family
 * medicine ") still count as the same visible row under one parent.
 */
function childTitleKey(title) {
  return String(title ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Reconstruct single-level nesting from the flat parent/child linkage. A child
 * (`_parent` set) is attached to the parent whose `_rawValue` matches *within the
 * same group*; a child whose parent is absent from this set is promoted to a
 * standalone top-level leaf (the expected behaviour when a sub-specialty matches
 * a search but its parent grouping is hidden).
 */
function nestItems(nodes) {
  const byValue = new Map();
  for (const node of nodes) {
    if (node._rawValue != null) {
      // First occurrence within a group wins, so the parent that ends up in
      // `topLevel` is the same node its children attach to.
      const key = linkKey(node.group, node._rawValue);
      if (!byValue.has(key)) {
        byValue.set(key, node);
      }
    }
  }

  const topLevel = [];
  // Per-parent set of already-attached child identities. The source data can emit
  // the same child under a parent more than once — either with the same `value`
  // (e.g. a specialty repeated across paths) or, crucially, with *different* values
  // but the same display title. The latter happens when one "Family Medicine" row
  // is a plain specialty and another is its practicing-specialty or synonym
  // resolution: distinct `Specialty-<id>` values, identical label. Both read as one
  // duplicate row to the user, so collapse on value OR normalised title.
  const seenChildren = new Map();
  for (const node of nodes) {
    const parent = node._parent != null ? byValue.get(linkKey(node.group, node._parent)) : undefined;
    if (parent && parent !== node) {
      let seen = seenChildren.get(parent);
      if (!seen) {
        seen = new Set();
        seenChildren.set(parent, seen);
      }
      const valueKey = `v:${node.value}`;
      const titleKey = `t:${childTitleKey(node.title)}`;
      if (!seen.has(valueKey) && !seen.has(titleKey)) {
        seen.add(valueKey);
        seen.add(titleKey);
        (parent.children ??= []).push(stripLinkage(node));
        parent.hasChildren = true;
      }
      continue;
    }
    topLevel.push(node);
  }

  return ensureUniqueValues(topLevel.map(stripLinkage));
}

/**
 * Guarantee every rendered node has a unique `value`. The source data emits the
 * same specialty under more than one heading — e.g. "Primary Care" is a parent
 * in both "Common Specialties" and "All Specialties", and a child like "Family
 * Medicine" recurs under several parents and again as its own top-level row. The
 * accordion (`AccordionContainer`) keys its expand set, children cache and row
 * `:key` by item value, so duplicate values collide: expanding one occurrence
 * leaks into the other and Vue drops the duplicate-keyed rows, leaving the second
 * parent rendered empty.
 *
 * The semantic value lives on `payload.value` for routing, so the node's own
 * `value` only needs to be a unique identity. First occurrence keeps its raw
 * value (so the common single-occurrence case is unchanged); later collisions get
 * a `#n` suffix. Walked in render order (parent then its children) for stable keys.
 */
function ensureUniqueValues(nodes) {
  const seen = new Set();
  const unique = (value) => {
    let candidate = value;
    let n = 2;
    while (seen.has(candidate)) {
      candidate = `${value}#${n++}`;
    }
    seen.add(candidate);
    return candidate;
  };

  for (const node of nodes) {
    node.value = unique(node.value);
    if (node.children) {
      for (const child of node.children) {
        child.value = unique(child.value);
      }
    }
  }
  return nodes;
}

/** Drop the private linkage keys once nesting is resolved. */
function stripLinkage(node) {
  delete node._rawValue;
  delete node._parent;
  return node;
}

/**
 * Substring match against a built node's title, keywords, and (recursively) its
 * children — the same shape `<sp-quantum-select>` filters on. Shared by the store
 * (`hasLocalMatch`), `<sp-finder-omnisearch>`, and the provider facet so the client-side
 * matching stays in one place.
 *
 * @param {Object} item - a built quantum-select node
 * @param {String} needle - a pre-lowercased query
 * @returns {Boolean}
 */
export function itemMatchesQuery(item, needle) {
  if (item.title?.toLowerCase().includes(needle)) {
    return true;
  }
  if (item.keywords?.some((keyword) => keyword?.toLowerCase().includes(needle))) {
    return true;
  }
  return Boolean(item.children?.some((child) => itemMatchesQuery(child, needle)));
}

/**
 * Filter built nodes by a query, optionally capped. Binding the entire search set
 * (thousands of nodes) to the accordion freezes the main thread, so callers cap
 * the count.
 *
 * @param {Array<Object>} items
 * @param {String} query
 * @param {{ limit?: number }} [options]
 * @returns {Array<Object>} matches (empty for a blank query)
 */
export function filterItems(items, query, { limit = Infinity } = {}) {
  const needle = (query ?? "").trim().toLowerCase();
  if (!needle) {
    return [];
  }

  const results = [];
  for (const item of items ?? []) {
    if (itemMatchesQuery(item, needle)) {
      results.push(item);
    }
    if (results.length >= limit) {
      break;
    }
  }

  return results;
}

/**
 * Flatten nested search nodes into one leaf per node for the fuzzy launcher, so a
 * matching subspecialty surfaces on its own rather than only under an expanded
 * parent. Leaf copies drop `children`/`hasChildren`, so the flat list renders no
 * expand carets. Node values are already globally unique (see `ensureUniqueValues`),
 * so flattening introduces no key collisions.
 *
 * @param {Array<Object>} nodes - nested built nodes (`searchItems`)
 * @returns {Array<Object>} one leaf per node, depth-first
 */
export function flattenSearchItems(nodes) {
  const flat = [];
  const walk = (node) => {
    if (!node) {
      return;
    }
    const { children } = node;
    const leaf = { ...node };
    delete leaf.children;
    delete leaf.hasChildren;
    flat.push(leaf);
    (children ?? []).forEach(walk);
  };
  (nodes ?? []).forEach(walk);
  return flat;
}
